import { MouseEventHandler, ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { twMerge } from 'tailwind-merge';
import Icon, { IconKey } from './Icon';
import LockedLink from './LockedLink';

interface Props {
    to: string;
    description?: ReactNode;
    iconKey?: IconKey;
    className?: string;
    children: ReactNode;
    onClick?: MouseEventHandler;
    disabled?: boolean;
    blocked?: boolean;
    blockedInformation?: string;
    state?: Record<string, string | boolean>;
}

const RouteLink = ({
    to,
    description,
    iconKey,
    className,
    children,
    onClick,
    disabled = false,
    blocked = false,
    blockedInformation,
    state = {},
}: Props) => {
    const { extensionKey } = useParams<{ extensionKey: ExtensionKey }>();

    if (to.startsWith('/settings/:extensionKey')) {
        to = extensionKey
            ? to.replace('/settings/:extensionKey', `/settings/${extensionKey}`)
            : to;
    }

    if (disabled) {
        return (
            <div
                className={twMerge(
                    'flex h-13 w-full items-center justify-between rounded-sm px-2 text-gray-400 hover:bg-gray-100',
                    className,
                )}
            >
                <div className="flex items-center gap-x-3">
                    {iconKey ? <Icon iconKey={iconKey} /> : null}
                    {children}
                </div>
                <div className="flex items-center gap-x-1">
                    <div>
                        <div className="text-sm text-gray-400">
                            {description}
                        </div>
                    </div>
                    <Icon
                        iconKey="chevron_right"
                        className="rtl:scale-x-[-1]"
                    />
                </div>
            </div>
        );
    }

    if (blocked) {
        return (
            <div
                className={twMerge(
                    'flex w-full cursor-not-allowed flex-col items-start justify-between gap-y-2 rounded-sm bg-gray-100 py-4 hover:bg-gray-100',
                    className,
                )}
            >
                <LockedLink
                    className={twMerge(blockedInformation ? 'py-0' : '')}
                >
                    {children}
                </LockedLink>
                {blockedInformation && (
                    <p className="px-2 text-xs text-gray-400">
                        {blockedInformation}
                    </p>
                )}
            </div>
        );
    }
    const linkTo =
        state && Object.keys(state).length > 0
            ? { pathname: to, state }
            : { pathname: to };

    return (
        <Link to={linkTo} onClick={onClick}>
            <div
                className={twMerge(
                    'flex h-13 w-full items-center justify-between rounded-sm px-2 hover:bg-gray-100',
                    className,
                )}
            >
                <div className="flex items-center gap-x-3">
                    {iconKey ? <Icon iconKey={iconKey} /> : null}
                    {children}
                </div>
                <div className="flex items-center gap-x-1">
                    <div>
                        <div className="text-end text-sm text-gray-500">
                            {description}
                        </div>
                    </div>
                    <Icon
                        iconKey="chevron_right"
                        className="rtl:scale-x-[-1]"
                    />
                </div>
            </div>
        </Link>
    );
};

export default RouteLink;
